import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import TeacherStats from "../components/child/TeacherStats";
import TeacherNotifications from "../components/child/TeacherNotifications";
import WelcomeBanner from "../components/child/WelcomeBanner";
import FinanceStats from '../components/FinanceStats';
import { eventsAtom, settingsAtom } from "../utils/atoms";
import { fetchCalendarEvents, eventTypesTexts } from "../utils/requests/events";

const TeacherHomePage = () => {
    const [events, setEvents] = useAtom(eventsAtom);
    const [settings] = useAtom(settingsAtom);
    const [todayEvents, setTodayEvents] = useState([]);
    const [stats, setStats] = useState({
        totalStudents: 0,
        totalLessons: 0,
        totalTests: 0,
        totalInternalTests: 0,
        totalIncome: 0,
        todayLessons: 0,
        upcomingLessons: 0
    });

    const userDep = settings?.user?.id;

    // Calculate statistics from events
    useEffect(() => {
        if (events.length > 0) {
            const today = new Date().toISOString().slice(0, 10);
            const todayEvents = events.filter(event =>
                event?.start?.startsWith?.(today)
            );

            const upcomingEvents = events.filter(event =>
                new Date(event.start) > new Date()
            );

            const lessons = events.filter(event => event.extendedProps?.type === 'lesson');
            const internalTests = events.filter(event => event.extendedProps?.type === 'internalTests');
            const tests = events.filter(event =>
                event.extendedProps?.type === 'test'
            );

            const totalIncome = events.reduce((sum, event) => {
                return sum + (event.extendedProps?.price || 0);
            }, 0);

            // Get unique students
            const uniqueStudents = new Set();
            events.forEach(event => {
                if (event.extendedProps?.student?.id) {
                    uniqueStudents.add(event.extendedProps.student.id);
                }
            });

            setStats({
                totalStudents: uniqueStudents.size,
                totalLessons: lessons.length,
                totalTests: tests.length,
                totalInternalTests: internalTests.length,
                totalIncome,
                todayLessons: todayEvents.filter(e => e.extendedProps?.type === 'lesson').length,
                upcomingLessons: upcomingEvents.filter(e => e.extendedProps?.type === 'lesson').length
            });

            setTodayEvents(todayEvents);
        }
    }, [events]);

    // Fetch current month's events on component mount
    useEffect(() => {
        async function fetchCurrentMonth() {
            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            const startDate = startOfMonth.toISOString().slice(0, 10);
            const endDate = endOfMonth.toISOString().slice(0, 10);

            const data = await fetchCalendarEvents(startDate, endDate);
            setEvents(data);
        }

        async function fetchToday() {
            const today = new Date();
            const startDate = today.toISOString().slice(0, 10);
            const endDate = startDate;
            const data = await fetchCalendarEvents(startDate, endDate);
            setTodayEvents(data);
        }

        if (userDep) {
            fetchCurrentMonth();
            fetchToday();
        }
    }, [userDep, setEvents]);

    if (!userDep) return null;

    const StatCard = ({ title, value, icon, color, subtitle, link }) => (
        <div className="col-xxl-3 col-xl-4 col-sm-6">
            <div className={`card p-3 shadow-2 radius-8 h-100 bg-gradient-end-${color}`}>
                <div className="card-body p-0">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                        <div className="d-flex align-items-center gap-2">
                            <span className={`mb-0 w-48-px h-48-px bg-${color}-100 text-${color}-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0`}>
                                <Icon icon={icon} className="icon" />
                            </span>
                            <div>
                                <h6 className="fw-semibold mb-2">{value}</h6>
                                <span className="fw-medium text-secondary-light text-sm">
                                    {title}
                                </span>
                            </div>
                        </div>
                    </div>
                    {subtitle && (
                        <p className="text-sm mb-0">
                            {subtitle}
                        </p>
                    )}
                    {link && (
                        <Link to={link} className="text-primary-600 hover-text-primary d-flex align-items-center gap-1 mt-2">
                            צפה בכל
                            <Icon icon="solar:alt-arrow-right-linear" className="icon" />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );

    const TodayEventsList = () => (
        <div className="card h-100">
            <div className="card-header border-bottom d-flex align-items-center flex-wrap gap-2 justify-content-between">
                <h6 className="fw-bold text-lg mb-0">אירועים היום</h6>
                <Link to="/calendar" className="text-primary-600 hover-text-primary d-flex align-items-center gap-1">
                    צפה בלוח שנה
                    <Icon icon="solar:alt-arrow-right-linear" className="icon" />
                </Link>
            </div>
            <div className="card-body p-24">
                {todayEvents.length === 0 ? (
                    <div className="text-center py-4">
                        <Icon icon="mdi:calendar-blank" className="text-4xl text-secondary-light mb-2" />
                        <p className="text-secondary-light">אין אירועים להיום</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">שעה</th>
                                    <th scope="col">סוג</th>
                                    <th scope="col">תלמיד</th>
                                    <th scope="col" className="text-center">סטטוס</th>
                                </tr>
                            </thead>
                            <tbody>
                                {todayEvents.slice(0, 5).map((event, idx) => (
                                    <tr key={event.id || idx}>
                                        <td>
                                            {new Date(event.start).toLocaleTimeString('he-IL', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td>
                                            <span className="badge bg-primary">
                                                {eventTypesTexts[event.extendedProps?.type] || 'אירוע'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden bg-primary-100 d-flex align-items-center justify-content-center">
                                                    <Icon icon="mdi:account" className="text-primary-600" />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="text-md mb-0 fw-medium">
                                                        {event.title}
                                                    </h6>
                                                    {event.extendedProps?.student?.phoneNumber && (
                                                        <span className="text-sm text-secondary-light fw-medium">
                                                            {event.extendedProps.student.phoneNumber}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <span className="bg-success-focus text-success-main px-24 py-4 rounded-pill fw-medium text-sm">
                                                מתוכנן
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <MasterLayout>
            <Breadcrumb title="ראשי" />

            <div className="container-fluid">
                {/* Welcome Banner */}
                <WelcomeBanner settings={settings} stats={stats} />

                {/* Statistics Cards */}
                <div className="row gy-4 mb-20">
                    <StatCard
                        title="סה״כ תלמידים פעילים החודש"
                        value={stats.totalStudents}
                        icon="mdi:account-group"
                        color="cyan"
                    />
                    <StatCard
                        title="סה״כ שיעורים החודש"
                        value={stats.totalLessons}
                        icon="mdi:car"
                        color="primary"
                    />
                    <StatCard
                        title="סה״כ טסטים החודש"
                        value={stats.totalTests}
                        icon="mdi:clipboard-check"
                        color="success"
                    />
                    <StatCard
                        title="סה״כ טסטים פנימיים"
                        value={stats.totalInternalTests}
                        icon="mdi:clipboard-check"
                        color="warning"

                    />
                </div>

                {/* Main Content */}
                <div className="row gy-4">
                    <div className="col-12">
                        <div className="row gy-4 gx-4">
                            {/* Notifications */}
                            <div className="col-12 col-md-4">
                                <TeacherNotifications events={events} settings={settings} />
                            </div>
                            {/* Today's Events - now takes the place of QuickActions */}
                            <div className="col-12 col-md-8">
                                <TodayEventsList />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Statistics */}
                <div className="row gy-4 mt-4">
                    <div className="col-12">
                        <TeacherStats stats={stats} events={events} />
                    </div>
                </div>
            </div>

            {/* Finance Statistics */}
            <FinanceStats />
        </MasterLayout>
    );
};

export default TeacherHomePage; 